import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { FormattedPrice } from '@10xLabs/react-web/components/FormattedPrice'
import { getBookingAmountByTickets } from 'resolvers/bookings'
import { sanitizePrice } from 'utils/math'
import { ReactElement } from 'react'
import { IBookingPaymentsSummaryProps } from 'features/Bookings/components/BookingPaymentsList/Summary/types'
import { GTBookingV2PaymentStatus } from 'gql/types.generated'

const Table = styled.table<TSFixMe>`
  border-collapse: collapse;
`

const Tf = styled.td<TSFixMe>`
  font-size: 16px;
  font-weight: 500;
  color: #1b1b3d;
  text-transform: uppercase;

  ${(props): TSFixMe => {
    if (props.isFirstRow && props.isFirst) {
      return `
        padding: 15px 40px 3px 0;
      `
    } else if (props.isFirstRow) {
      return `
        padding: 15px 40px 3px 40px;
      `
    } else if (props.isFirst) {
      return `
        padding: 3px 40px 3px 0;
      `
    } else {
      return `
        padding: 3px 40px;
      `
    }
  }}
`

export const BookingPaymentsSummary = ({
  booking,
  editPriceDisabled,
  tripChangeProposalId,
  newTickets
}: IBookingPaymentsSummaryProps): ReactElement => {
  const bookingAmount = getBookingAmountByTickets({ tickets: booking.tickets })
  const newAmount = getBookingAmountByTickets({ tickets: newTickets })
  const paidAmount = booking.payments.reduce((result: TSFixMe, payment: TSFixMe): TSFixMe => {
    if (payment.status === GTBookingV2PaymentStatus.Paid || payment.status === GTBookingV2PaymentStatus.Refunded) {
      return result + payment.amount
    }

    return result
  }, 0)
  const totalRefundedAmount = booking.refunds.reduce((result: TSFixMe, refund: TSFixMe): TSFixMe => {
    return result + refund.amount
  }, 0)
  const pendingPaymentAmount =
    newAmount.subtotal + newAmount.vat - newAmount.discount - (paidAmount - totalRefundedAmount)
  const oldBookingAmount = bookingAmount.subtotal + bookingAmount.vat - bookingAmount.discount
  const newBookingAmount = newAmount.subtotal + newAmount.vat - newAmount.discount
  const hasNewPrices = newBookingAmount !== oldBookingAmount
  const showNewPaymentAmount = !!tripChangeProposalId || hasNewPrices
  const pricesCurrency = booking.currency.code === 'USD' ? 'USD' : undefined

  return (
    <Table>
      <tbody>
        <tr>
          <Tf isFirstRow isFirst>
            <FormattedMessage id="bookings.edit.payments.totalOriginalAmount" defaultMessage="Original total amount" />
          </Tf>
          <Tf isFirstRow>
            <FormattedPrice
              value={sanitizePrice(bookingAmount.subtotal + bookingAmount.vat)}
              currency={pricesCurrency}
            />
          </Tf>
        </tr>
        <tr>
          <Tf isFirst>
            <FormattedMessage
              id="bookings.edit.payments.totalOriginalDiscount"
              defaultMessage="Original total discount"
            />
          </Tf>
          <Tf>
            {bookingAmount.discount > 0 ? '-' : ''}
            <FormattedPrice value={sanitizePrice(bookingAmount.discount)} currency={pricesCurrency} />
          </Tf>
        </tr>
        <tr>
          <Tf />
        </tr>
        {!editPriceDisabled && (
          <tr>
            <Tf isFirst>
              <FormattedMessage id="bookings.edit.payments.newTotalAmount" defaultMessage="New total amount" />
            </Tf>
            <Tf>
              {showNewPaymentAmount ? (
                <FormattedPrice value={sanitizePrice(newAmount.subtotal + newAmount.vat)} currency={pricesCurrency} />
              ) : (
                '-'
              )}
            </Tf>
          </tr>
        )}
        {!editPriceDisabled && (
          <tr>
            <Tf isFirst>
              <FormattedMessage id="bookings.edit.payments.newTotalDiscount" defaultMessage="New total discount" />
            </Tf>
            <Tf>
              {showNewPaymentAmount ? (
                <>
                  {newAmount.discount > 0 ? '-' : ''}
                  <FormattedPrice value={sanitizePrice(newAmount.discount)} currency={pricesCurrency} />
                </>
              ) : (
                '-'
              )}
            </Tf>
          </tr>
        )}
        <tr>
          <Tf />
        </tr>
        <tr>
          <Tf isFirst>
            <FormattedMessage id="bookings.edit.payments.totalPaidAmount" defaultMessage="Total paid amount" />
          </Tf>
          <Tf>
            <FormattedPrice value={sanitizePrice(paidAmount)} currency={pricesCurrency} />
          </Tf>
        </tr>
        <tr>
          <Tf isFirst>
            <FormattedMessage id="bookings.edit.payments.totalCancelledAmount" defaultMessage="Total refunded amount" />
          </Tf>
          <Tf>
            <FormattedPrice value={sanitizePrice(totalRefundedAmount)} currency={pricesCurrency} />
          </Tf>
        </tr>
        {!editPriceDisabled && (
          <tr>
            <Tf isFirst>
              <FormattedMessage id="bookings.edit.payments.finalTotalAmount" defaultMessage="Final total amount" />
            </Tf>
            <Tf>
              <FormattedPrice
                value={sanitizePrice(newAmount.subtotal + newAmount.vat - newAmount.discount)}
                currency={pricesCurrency}
              />
            </Tf>
          </tr>
        )}
        <tr>
          <Tf isFirst>
            <FormattedMessage id="bookings.edit.payments.pendingTotalAmount" defaultMessage="Pending total amount" />
          </Tf>
          <Tf>
            {pendingPaymentAmount < 0 && (
              <>
                -<FormattedPrice value={sanitizePrice(-pendingPaymentAmount)} currency={pricesCurrency} />
              </>
            )}
            {pendingPaymentAmount >= 0 && (
              <FormattedPrice value={sanitizePrice(pendingPaymentAmount)} currency={pricesCurrency} />
            )}
          </Tf>
        </tr>
      </tbody>
    </Table>
  )
}
